import React, { useCallback, useRef, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import chevronDownSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import Menu, { MenuItem } from '@amzn/meridian/menu';
import Row from '@amzn/meridian/row';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import {
  ALTERNATIVE_PLACEMENT_RECOMMENDED,
  APPROVED_TWP,
  ASSOCIATE_DECISION_PENDING,
  DENIED_TWP,
  DLS_REFERRAL_RECEIVED,
  INDEFINITE_RESTRICTIONS,
  PLACEMENT_DECISION_PENDING,
  TRANSFER_PENDING,
  TWP_BUT_PENDING_UPDATED_RESTRICTIONS,
} from 'src/config/AccommodationStatusNames';
import { COUNTRY_ID_MAPPING } from 'src/config/CountryIdMapping';
import * as LDAP from 'src/config/LDAP';
import JmrService from 'src/services/jmr/jmrService';
import { setJmrFlowType } from 'src/store/newJmrPageSlice/newJmrPageSlice';
import {
  setIsAlternativePlacementModalOpen,
  setIsConfirmDeleteModalOpen,
  setIsConfirmUpdateRestrictionsModalOpen,
  setIsEmailModalOpen,
  setIsInitiateTransferModalOpen,
  setIsOomdModalOpen,
  setIsRestrictionEntriesErrorModalOpen,
  setIsUpdateRestrictionDatesModalOpen,
  setIsUpdateStatusModalOpen,
} from 'src/store/placementDetailsPageSlice/placementDetailsPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { createFileUrl, downloadFile } from 'src/utils/fileUtils';
import { JmrFlowTypes } from 'src/utils/jmrUtils';

interface TopOptionsProps {
  doesPlacementHaveRestrictionErrors: boolean;
  openTwpaModal: () => void;
}

/**
 * Default component
 */
const TopOptions = ({ doesPlacementHaveRestrictionErrors, openTwpaModal }: TopOptionsProps) => {
  const { placementId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const exportButtonRef = useRef();
  const updateButtonRef = useRef();
  const { featureFlags, ldap } = useAppSelector((state) => state.user);
  const { placementDetails, accommodationStatus } = useAppSelector((state) => state.placementDetailsPage);
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const [isUpdateMenuOpen, setIsUpdateMenuOpen] = useState(false);
  const onClickExportButton = useCallback(() => setIsExportMenuOpen(true), []);
  const onCloseExportMenu = useCallback(() => setIsExportMenuOpen(false), []);
  const onClickUpdateButton = useCallback(() => setIsUpdateMenuOpen(true), []);
  const onCloseUpdateMenu = useCallback(() => setIsUpdateMenuOpen(false), []);
  const isAdmin = ldap.includes(LDAP.ADMIN);

  // These statuses are allowed to enter the update placement flow
  const STATUSES_ALLOWED_TO_UPDATE_PLACEMENTS = featureFlags.DECISION_PORTAL
    ? [
        PLACEMENT_DECISION_PENDING,
        ASSOCIATE_DECISION_PENDING,
        APPROVED_TWP,
        INDEFINITE_RESTRICTIONS,
        TWP_BUT_PENDING_UPDATED_RESTRICTIONS,
        DLS_REFERRAL_RECEIVED,
        DENIED_TWP,
      ]
    : [PLACEMENT_DECISION_PENDING, ALTERNATIVE_PLACEMENT_RECOMMENDED];

  const downloadJmr = useCallback(async () => {
    const { data } = await JmrService.downloadJMR(placementDetails.newestJmrFileName);
    if (data) {
      downloadFile(createFileUrl(data, 'application/pdf'), 'jmr-report.pdf');
    }
  }, [placementDetails?.newestJmrFileName]);

  return (
    <Row maxWidth="100%" widths={['', 'fill']}>
      {/* Associate/Placement Info */}
      <Text type="d100" data-cy="placement-details__name-header">
        {placementDetails.associateFullName || placementDetails.login || ''}
      </Text>
      <div>
        <Tag type="warning" data-cy="placement-details__status-tag">
          {placementDetails?.accommodationStatus?.status}
        </Tag>
      </div>

      {/* Delete Placement */}
      {isAdmin && (
        <Button
          data-cy="placement-details__delete-btn"
          type="link"
          onClick={() => dispatch(setIsConfirmDeleteModalOpen(true))}
        >
          Delete Placement
        </Button>
      )}

      {/* Send Email/Update/Export */}
      {placementDetails?.accommodationStatus?.status !== TRANSFER_PENDING && (
        <>
          <Button
            type="secondary"
            onClick={() => dispatch(setIsEmailModalOpen(true))}
            data-cy="placement-detail-send-email__btn"
          >
            Send Email
          </Button>
          <Button
            id="update-placement-open-menu-btn"
            data-cy="placement-detail__update-btn"
            type="primary"
            ref={updateButtonRef}
            onClick={() => onClickUpdateButton()}
          >
            Update
            <Icon tokens={chevronDownSmallTokens} />
          </Button>
          <Button type="primary" ref={exportButtonRef} onClick={() => onClickExportButton()}>
            Export
            <Icon tokens={chevronDownSmallTokens} />
          </Button>
        </>
      )}

      {/* Accept Transfer */}
      {placementDetails?.accommodationStatus?.status === TRANSFER_PENDING && (
        <Button
          type="primary"
          data-cy="placement-details__accept-transfer-btn"
          onClick={() => {
            dispatch(setJmrFlowType(JmrFlowTypes.CompleteTransfer));
            navigate(`/newJMR/${placementId}/update/${JmrFlowTypes.CompleteTransfer}`);
          }}
        >
          Accept Transfer
        </Button>
      )}

      {/* Update Menu */}
      <Menu
        anchorNode={updateButtonRef.current}
        open={isUpdateMenuOpen}
        position="bottom"
        onClose={onCloseUpdateMenu}
        data-cy="placement-detail__update-menu"
      >
        {(accommodationStatus?.length ?? 0) > 0 && (
          <MenuItem onClick={() => dispatch(setIsUpdateStatusModalOpen(true))} data-cy="placement-details__status-btn">
            Status
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            if (doesPlacementHaveRestrictionErrors) {
              dispatch(setIsRestrictionEntriesErrorModalOpen(true));
            } else {
              dispatch(setIsUpdateRestrictionDatesModalOpen(true));
            }
          }}
          data-cy="placement-details__extend-end-date-btn"
        >
          Restriction Dates
        </MenuItem>
        <MenuItem
          onClick={() => dispatch(setIsConfirmUpdateRestrictionsModalOpen(true))}
          data-cy="placement-details__restrictions-btn"
        >
          Restrictions
        </MenuItem>
        {STATUSES_ALLOWED_TO_UPDATE_PLACEMENTS.includes(placementDetails?.accommodationStatus?.status) && (
          <MenuItem
            onClick={() => {
              if (doesPlacementHaveRestrictionErrors) {
                dispatch(setIsRestrictionEntriesErrorModalOpen(true));
              } else {
                dispatch(setIsAlternativePlacementModalOpen(true));
              }
            }}
            data-cy="placement-details__alternative-placement-btn"
          >
            Placement Selection(s)
          </MenuItem>
        )}
        {featureFlags.PLACEMENT_TRANSFER && placementDetails.accommodationType === 'AUSTIN' && (
          <MenuItem
            onClick={() => {
              if (doesPlacementHaveRestrictionErrors) {
                dispatch(setIsRestrictionEntriesErrorModalOpen(true));
              } else {
                dispatch(setIsInitiateTransferModalOpen(true));
              }
            }}
            data-cy="placement-details__initiate-transfer-btn"
          >
            Initiate Transfer
          </MenuItem>
        )}
      </Menu>

      {/* Export Menu */}
      <Menu anchorNode={exportButtonRef.current} open={isExportMenuOpen} position="bottom" onClose={onCloseExportMenu}>
        {placementDetails.newestJmrFileName && <MenuItem onClick={downloadJmr}>Export JMR</MenuItem>}
        {placementDetails?.countryId === COUNTRY_ID_MAPPING.CANADA_COUNTRY_ID ? (
          <MenuItem onClick={() => dispatch(setIsOomdModalOpen(true))}>OOMD PDF</MenuItem>
        ) : (
          <MenuItem onClick={openTwpaModal}>TWPA PDF</MenuItem>
        )}
      </Menu>
    </Row>
  );
};

export default TopOptions;
